import {
  AuthBySocialNetworkInput,
  Social_Network_Provider,
  useLoginBySocialNetworkMutation,
} from '@generated/graphql';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import VkLogoIcon from 'assets/icons/VkLogo';
import YandexLogoIcon from 'assets/icons/YandexLogo';
import { PopupMessageType } from 'constants/oAuth';
import ROUTES from 'constants/routes';
import { useParseOAuthRequestEndData } from 'pages/onboarding/OAuth/hooks';
import { OAuthRequestEndEventData } from 'pages/onboarding/OAuth/types';
import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import TelegramLoginWidget from './TelegramLoginWidget';

interface SocialControlsProps {
  loginLoading: boolean;
  setLoginLoading: Dispatch<SetStateAction<boolean>>;
}

type OAuthProviderName = 'vk' | 'yandex';

interface OAuthProviderConfig {
  provider: Social_Network_Provider;
  providerName: OAuthProviderName;
  popupName: string;
  authPath: string;
  successType: PopupMessageType;
  errorType: PopupMessageType;
}

const OAUTH_PROVIDER_CONFIG: Record<OAuthProviderName, OAuthProviderConfig> = {
  yandex: {
    provider: Social_Network_Provider.Yandex,
    providerName: 'yandex',
    popupName: 'yandex-oauth',
    authPath: '/auth/yandex',
    successType: PopupMessageType.yandexOAuthSuccess,
    errorType: PopupMessageType.yandexOAuthError,
  },
  vk: {
    provider: Social_Network_Provider.Vk,
    providerName: 'vk',
    popupName: 'vk-oauth',
    authPath: '/auth/vk',
    successType: PopupMessageType.vkOAuthSuccess,
    errorType: PopupMessageType.vkOAuthError,
  },
};

const CODE_VERIFIER_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';

const generateCodeVerifier = (length = 64) => {
  const randomValues = new Uint8Array(length);
  window.crypto.getRandomValues(randomValues);

  return Array.from(
    randomValues,
    (value) => CODE_VERIFIER_ALPHABET[value % CODE_VERIFIER_ALPHABET.length],
  ).join('');
};

const SocialControls = ({ loginLoading, setLoginLoading }: SocialControlsProps) => {
  const parseOAuthRequestEndData = useParseOAuthRequestEndData();
  const [loginBySocialNetwork] = useLoginBySocialNetworkMutation();
  const { t } = useTranslation();

  const onSocialAuth = (providerName: OAuthProviderName) => {
    const config = OAUTH_PROVIDER_CONFIG[providerName];
    const isVkProvider = config.provider === Social_Network_Provider.Vk;
    const codeVerifier = isVkProvider ? generateCodeVerifier() : undefined;
    const state = isVkProvider ? generateCodeVerifier() : undefined;
    const callbackUrl = new URL(`${window.location.origin}${ROUTES.OAUTH}`);

    if (!isVkProvider) {
      callbackUrl.searchParams.set('popup', '1');
      callbackUrl.searchParams.set('openerOrigin', window.location.origin);
      callbackUrl.searchParams.set('provider', config.providerName);
    }

    const authUrl = new URL(config.authPath, process.env.REACT_APP_API_URL);
    authUrl.searchParams.set('callbackUrl', callbackUrl.toString());
    if (codeVerifier) {
      authUrl.searchParams.set('codeVerifier', codeVerifier);
    }
    if (state) {
      authUrl.searchParams.set('state', state);
    }

    let closeWatcher: number | null = null;

    const onMessage = async (event: MessageEvent<OAuthRequestEndEventData>) => {
      if (event.origin !== window.location.origin) return;

      if (![config.successType, config.errorType].includes(event.data?.type)) return;

      if (closeWatcher !== null) {
        window.clearInterval(closeWatcher);
      }
      window.removeEventListener('message', onMessage);

      if (event.data.type === config.errorType) {
        setLoginLoading(false);
        parseOAuthRequestEndData(event.data);
        return;
      }

      if (!event.data.code) {
        setLoginLoading(false);
        parseOAuthRequestEndData({ error: 'Отсутствует code' });
        return;
      }

      try {
        const input: AuthBySocialNetworkInput = {
          code: event.data.code,
          provider: config.provider,
        };

        if (config.provider === Social_Network_Provider.Vk) {
          input.deviceId = event.data.deviceId;
          input.codeVerifier = event.data.codeVerifier || codeVerifier;
          input.redirectUri = event.data.redirectUri || callbackUrl.toString();
        }

        const res = await loginBySocialNetwork({
          variables: { input },
        });

        const accessToken = res.data?.loginBySocialNetwork.accessToken;
        const refreshToken = res.data?.loginBySocialNetwork.refreshToken;

        parseOAuthRequestEndData({
          payload: { accessToken, refreshToken },
        });
      } catch (error) {
        parseOAuthRequestEndData({
          error: 'Ошибка авторизации',
          errorInstance: error,
        });
      } finally {
        setLoginLoading(false);
      }
    };

    window.addEventListener('message', onMessage);
    setLoginLoading(true);

    const width = 520;
    const height = 720;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      authUrl.toString(),
      config.popupName,
      `width=${width},height=${height},left=${left.toFixed()},top=${top.toFixed()},menubar=no,toolbar=no,location=yes,status=no,resizable=yes,scrollbars=yes`,
    );

    if (!popup) {
      window.removeEventListener('message', onMessage);
      setLoginLoading(false);
      parseOAuthRequestEndData({ error: 'Браузер заблокировал всплывающее окно' });
      return;
    }

    closeWatcher = window.setInterval(() => {
      if (!popup.closed) return;
      window.clearInterval(closeWatcher as number);
      window.removeEventListener('message', onMessage);
      setLoginLoading(false);
    }, 500);
  };

  return (
    <Stack spacing={0.5} alignItems='center'>
      <Stack direction='row' alignItems='center' spacing={2} useFlexGap sx={{ width: '100%' }}>
        <Box sx={{ flex: 1, height: '1px', backgroundColor: '#E1E8FF' }} />
        <Typography sx={{ color: '#9AA8C7', fontSize: 16, whiteSpace: 'nowrap' }}>
          {t('loginPage.socialDivider')}
        </Typography>
        <Box sx={{ flex: 1, height: '1px', backgroundColor: '#E1E8FF' }} />
      </Stack>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='center'
        spacing={1.5}
        useFlexGap
        flexWrap='wrap'>
        <TelegramLoginWidget setLoginLoading={setLoginLoading} />
        <IconButton onClick={() => onSocialAuth('yandex')} loading={loginLoading} size='large'>
          <YandexLogoIcon fontSize='large' />
        </IconButton>
        <IconButton onClick={() => onSocialAuth('vk')} loading={loginLoading} size='large'>
          <VkLogoIcon fontSize='large' />
        </IconButton>
      </Stack>
    </Stack>
  );
};

export default SocialControls;
